import { useState, useCallback } from 'react';
import { AppHeader } from '@/components/layout/AppHeader';
import { usePendingAcknowledgements } from '@/hooks/usePendingAcknowledgements';
import { useMyAcknowledgedPolicies } from '@/hooks/useMyAcknowledgedPolicies';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, CheckCircle2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Lottie from 'lottie-react';
import successAnimation from '@/assets/animations/success.json';
import securitySafeAnimation from '@/assets/animations/security-safe.json';

export default function StaffDashboard() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: pendingAcks, isLoading: pendingLoading } = usePendingAcknowledgements();
  const { data: acknowledgedPolicies, isLoading: ackedLoading } = useMyAcknowledgedPolicies();

  const [acknowledgingId, setAcknowledgingId] = useState<string | null>(null);
  const [showSuccessOverlay, setShowSuccessOverlay] = useState(false);

  const handleAcknowledge = useCallback(async (versionId: string) => {
    if (!profile?.id) return;
    setAcknowledgingId(versionId);

    try {
      const { error } = await supabase
        .from('policy_acknowledgements')
        .insert({
          policy_version_id: versionId,
          acknowledged_by: profile.id,
        });

      if (error) {
        if (error.code === '23505') {
          toast.error('You have already acknowledged this version');
        } else {
          throw error;
        }
        setAcknowledgingId(null);
        return;
      }

      setShowSuccessOverlay(true);
    } catch (err: any) {
      toast.error(err.message || 'Failed to acknowledge policy');
      setAcknowledgingId(null);
    }
  }, [profile?.id]);

  const handleSuccessComplete = useCallback(() => {
    setShowSuccessOverlay(false);
    setAcknowledgingId(null);
    toast.success('Policy acknowledged successfully');
    queryClient.invalidateQueries({ queryKey: ['pending-acknowledgements'] });
    queryClient.invalidateQueries({ queryKey: ['my-acknowledged-policies'] });
  }, [queryClient]);

  const isLoading = pendingLoading || ackedLoading;
  const hasPending = pendingAcks && pendingAcks.length > 0;

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />

      <main className="container max-w-3xl mx-auto px-4 py-8">
        {/* Page Title */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-foreground">
            My Compliance
          </h1>
          <p className="text-sm mt-1 text-muted-foreground">
            Your personal compliance record
          </p>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {/* State 1: Pending Acknowledgements */}
            {hasPending ? (
              <Card className="mb-8 relative overflow-hidden border-2 border-[#339999]">
                {showSuccessOverlay && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/80 dark:bg-background/80">
                    <Lottie
                      animationData={successAnimation}
                      loop={false}
                      onComplete={handleSuccessComplete}
                      style={{ width: 160, height: 160 }}
                    />
                  </div>
                )}
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg font-semibold text-[#339999]">
                    Needs Your Acknowledgment
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {pendingAcks!.map((ack) => (
                    <div
                      key={ack.version_id}
                      className="flex items-center justify-between rounded-lg border dark:border-border p-4 bg-white dark:bg-card"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm truncate text-foreground">
                          {ack.policy_name}
                        </p>
                        <p className="text-xs mt-0.5 text-muted-foreground">
                          {ack.policy_category}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        disabled={acknowledgingId !== null}
                        onClick={() => handleAcknowledge(ack.version_id)}
                        className="ml-4 shrink-0 bg-[#339999] text-white hover:bg-[#2d8888]"
                      >
                        {acknowledgingId === ack.version_id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          'Acknowledge'
                        )}
                      </Button>
                    </div>
                  ))}
                </CardContent>
              </Card>
            ) : (
              /* State 2: All Caught Up */
              <div className="flex flex-col items-center justify-center py-12 mb-8">
                <Lottie
                  animationData={securitySafeAnimation}
                  loop={false}
                  style={{ width: 180, height: 180 }}
                />
                <h2 className="text-xl font-bold mt-4 text-foreground">
                  You're all caught up
                </h2>
                <p className="text-sm mt-1 text-muted-foreground">
                  All policies have been acknowledged
                </p>
              </div>
            )}

            {/* State 3: My Acknowledged Policies (always visible) */}
            {acknowledgedPolicies && acknowledgedPolicies.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold mb-4 text-foreground">
                  My Acknowledgements
                </h2>
                <div className="space-y-2">
                  {acknowledgedPolicies.map((policy) => (
                    <div
                      key={policy.policy_id}
                      className="flex items-center justify-between rounded-lg border dark:border-border p-4 bg-white dark:bg-card cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => navigate(`/policies/${policy.policy_id}`)}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm text-foreground">
                          {policy.policy_name}
                        </p>
                        <p className="text-xs mt-0.5 text-muted-foreground">
                          Acknowledged {format(new Date(policy.acknowledged_at), 'MMM d, yyyy · h:mm a')}
                        </p>
                      </div>
                      <Badge className="ml-4 shrink-0 bg-green-600 text-white hover:bg-green-600">
                        <CheckCircle2 className="h-3 w-3 mr-1" />
                        Acknowledged
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
